import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { clerkPlugin } from '@clerk/vue'
import App from './App.vue'
import router from './router'
import './styles/main.css'
import { initSentry, captureEvent } from './lib/sentry'
import { initializeGA4 } from './lib/analytics'

const app = createApp(App)

// Sentry error monitoring
initSentry(app, router)

// State management
app.use(createPinia())

// Clerk authentication
// VITE_CLERK_PUBLISHABLE_KEY is set in .env.local (dev) and Vercel dashboard (prod)
app.use(clerkPlugin, {
  publishableKey: import.meta.env.VITE_CLERK_PUBLISHABLE_KEY,
  afterSignInUrl: '/dashboard',
  afterSignUpUrl: '/dashboard',
  afterSignOutUrl: '/'
})

// Router
app.use(router)

// Global Vue error handler — captures to Sentry and shows fallback UI
app.config.errorHandler = (err, instance, info) => {
  console.error('Global error:', err)
  if (import.meta.env.VITE_ENVIRONMENT === 'production') {
    captureEvent(`Vue error: ${err.message}`, 'error', {
      component: instance?.$options.name || 'Unknown',
      errorInfo: info,
      stack: err.stack
    })
  }
  // Show fallback UI instead of blank page
  const appEl = document.getElementById('app')
  if (appEl && !appEl.querySelector('.sc-error-boundary')) {
    const el = document.createElement('div')
    el.className = 'sc-error-boundary'
    el.style.cssText = [
      'position:fixed', 'top:0', 'left:0', 'right:0', 'bottom:0',
      'display:flex', 'align-items:center', 'justify-content:center',
      'background:#fff', 'z-index:9999', 'font-family:system-ui,sans-serif'
    ].join(';')
    el.innerHTML = `
      <div style="text-align:center;max-width:420px;padding:2rem">
        <h2 style="font-size:1.5rem;font-weight:700;color:#111827">Something went wrong</h2>
        <p style="color:#6b7280;margin-top:0.5rem">
          Try refreshing the page. If the problem persists,
          <a href="/contact" style="color:#7c3aed;text-decoration:underline">contact us</a>.
        </p>
        <button
          onclick="location.reload()"
          style="margin-top:1.5rem;padding:0.75rem 1.5rem;background:#7c3aed;color:#fff;border:none;border-radius:0.5rem;cursor:pointer;font-size:1rem;font-weight:600">
          Refresh Page
        </button>
      </div>`
    appEl.appendChild(el)
  }
}

if (import.meta.env.PROD) {
  app.config.performance = true
}

app.mount('#app')

// Connect Clerk to the auth store once Clerk has loaded
// Uses window.Clerk which @clerk/vue sets after initialization
async function connectClerkToStore() {
  const { useAuthStore } = await import('./stores/auth')
  const pinia = app.config.globalProperties.$pinia
  const authStore = useAuthStore(pinia)

  const tryConnect = () => {
    if (window.Clerk?.loaded) {
      authStore.onClerkReady(window.Clerk)
      return true
    }
    return false
  }

  if (!tryConnect()) {
    // Clerk loads asynchronously — poll until ready
    const id = setInterval(() => {
      if (tryConnect()) clearInterval(id)
    }, 100)
    // Stop polling after 10 seconds regardless
    setTimeout(() => clearInterval(id), 10000)
  }
}

connectClerkToStore()

// Analytics (deferred for performance)
setTimeout(() => { initializeGA4() }, 1000)

if (import.meta.env.DEV) {
  console.log('🚀 SecondChance.dev mounted — Clerk + Render + PostgreSQL stack')
}
